// src/api/authApi.js

import httpClient from "./httpClient";

/**
 * Backend Authentication API
 * Handles all authentication-related API calls
 */
class BackendAuthAPI {
  /**
   * Sign up new user
   * Sends verification code to email
   * @param {object} userData - User's first name
   * @param {string} userData.firstName - User's first name
   * @param {string} userData.lastName - User's last name
   * @param {string} userData.email - User's email
   * @param {string} userData.password - User's password
   * @param {string} userData.passwordConfirm - Password confirmation
   * @returns {Promise<object>} User object and message
   */
  async signup(userData) {
    const response = await httpClient.post("/auth/signup", {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      password: userData.password,
      passwordConfirm: userData.passwordConfirm,
    });

    return {
      data: {
        user: response.data.data.user,
        message: response.data.message,
      },
    };
  }

  /**
   * Send verification code to email
   * Used for resending code or initial send
   * @param {string} email - User's email address
   * @returns {Promise<object>} Success message
   */
  async sendVerificationCode(email) {
    const response = await httpClient.post("/auth/send-verification-code", {
      email,
    });

    return {
      data: {
        message: response.data.message,
      },
    };
  }

  /**
   * Verify email with 6-digit code
   * Returns access token and sets refresh token in httpOnly cookie
   * @param {string} code - 6-digit verification code
   * @returns {Promise<object>} User object and access token
   */
  async verifyCode(code) {
    const response = await httpClient.post("/auth/verify-code", {
      code,
    });

    return {
      data: {
        user: response.data.data.user,
        accessToken: response.data.accessToken,
      },
    };
  }

  /**
   * Login user
   * Returns access token and sets refresh token in httpOnly cookie
   * @param {object} credentials - Login credentials
   * @param {string} credentials.email - User's email
   * @param {string} credentials.password - User's password
   * @return {Promise<object>} User object and access token
   */
  async login(credentials) {
    const response = await httpClient.post("/auth/login", {
      email: credentials.email,
      password: credentials.password,
    });

    return {
      data: {
        user: response.data.data.user,
        accessToken: response.data.accessToken,
      },
    };
  }

  /**
   * Refresh access token using httpOnly cookie
   * Uses httpOnly cookie automatically (withCredentials: true)
   * @returns {Promise<object>} New access token
   */
  async refreshToken() {
    const response = await httpClient.post("/auth/refresh-token");

    return {
      data: {
        accessToken: response.data.accessToken,
      },
    };
  }

  /**
   * Logout user
   * Backend clears httpOnly cookie
   * @returns {Promise<object>} Success message
   */
  async logout() {
    const response = await httpClient.post("/auth/logout");

    return {
      data: {
        message: response.data.message || "Logged out successfully",
      },
    };
  }

  /**
   * Get current authenticated user
   * Requires valid access token
   * @returns {Promise<object>} User object
   */
  async getCurrentUser() {
    const response = await httpClient.get("/users/me");

    return {
      data: {
        user: response.data.data.user,
      },
    };
  }

  /**
   * Update user profile
   * @param {objecct} updates - Profile updates
   * @returns {Promise<object>} Update user object
   */
  async updateProfile(updates) {
    const response = await httpClient.patch("/users", updates);

    return {
      data: {
        user: response.data.data.user,
      },
    };
  }

  /**
   * Upload user avatar photo
   * @param {File} file - The image file to upload
   * @returns {Promise<object>} Updated user object
   */
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append("photo", file);

    const response = await httpClient.patch("/users/upload-photo", formData);

    return {
      data: {
        photoUrl: response.data.data.photoUrl,
      },
    };
  }
  /**
   * Request password reset
   * Sends reset token to user's email
   * @param {string} email - User's email address
   * @returns {Promise<object>} Success message
   */
  async forgotPassword(email) {
    const response = await httpClient.post("/auth/forgot-password", {
      email,
    });

    return {
      data: {
        message: response.data.message,
      },
    };
  }

  /**
   * Reset password using token from email
   * Returns access token and user data (auto-login)
   * @param {string} token - Reset token from URL
   * @param {string} password - New password
   * @param {string} passwordConfirm - Password confirmation
   * @returns {Promise<object>} User object and access token
   */
  async resetPassword(token, password, passwordConfirm) {
    const response = await httpClient.patch(`/auth/reset-password/${token}`, {
      password,
      passwordConfirm,
    });

    return {
      data: {
        user: response.data.data.user,
        accessToken: response.data.accessToken,
      },
    };
  }
}

export const authAPI = new BackendAuthAPI();
